import { AxiosError, AxiosRequestConfig, Method } from 'axios';
import axios from 'axios-observable';
import { IRestApi, IRestApiOptions, IRestRequestOptions } from '@vivli/core/infrastructure/interface';
import { from, Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { useRef } from 'react';
import { useDefaultApiOptions } from '@vivli/shared/infrastructure/hook';

const startTimeHeader = 'request-startTime';

export interface IRestApiProps {
    endpoint: string;
    useSecureApi?: boolean;
    overrideOptions?: IRestApiOptions;
    getTokenRefresh?: () => Promise<string>;
}

export const useRestApi = (props?: IRestApiProps): IRestApi => {
    // allow override of default options or null
    const { overrideOptions, getTokenRefresh, endpoint } = props || {};

    const options = useDefaultApiOptions({ overrideOptions, endpoint });
    const axiosInstanceRef = useRef<axios>(null);

    // Initialize axios instance once
    if (!axiosInstanceRef.current) {
        const { retryCalls, retryDelay, headers, trackApiCall } = options;
        axiosInstanceRef.current = axios.create({ baseURL: endpoint, headers });

        axiosInstanceRef.current.interceptors.request.use((config) => {
            config.headers[startTimeHeader] = new Date().getTime();
            return config;
        });

        axiosInstanceRef.current.interceptors.response.use((response) => {
            const requestTime = Math.abs(new Date().getTime() - response.config.headers[startTimeHeader]);

            // added tsignore in order to check the window object to verify if this is a cypress call or not
            // do not try to call trackApiCall if cypress - JM 7-25-2022
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (trackApiCall && !window.Cypress) {
                trackApiCall(requestTime);
            }
            return response;
        });
    }

    const buildRequest = <T>({
        url,
        method,
        data,
        options: requestOptions,
        authToken
    }: {
        url: string;
        method: Method;
        data?: any;
        options?: IRestRequestOptions;
        authToken: string;
    }): Observable<T> => {
        //isJsonInput is true if the request data is suppose to be in json format,
        //false - when the data is FormData or any other type of data - not json.
        const isJsonInput =
            (requestOptions?.additionalHeaders &&
                (!Object.keys(requestOptions.additionalHeaders).some((key) => key === 'Content-Type') ||
                    requestOptions.additionalHeaders['Content-Type'].toString().toLower().indexOf('json') >= 0)) ??
            true;
        let requestConfig: AxiosRequestConfig = {
            url,
            method,
            headers: requestOptions?.additionalHeaders,
            data: isJsonInput ? JSON.stringify(data) : data
        };

        if (requestOptions?.baseUrl) {
            requestConfig = {
                ...requestConfig,
                baseURL: requestOptions.baseUrl
            };
        }

        if (requestOptions?.responseType) {
            requestConfig = {
                ...requestConfig,
                responseType: requestOptions.responseType
            };
        }

        if (authToken) {
            requestConfig = {
                ...requestConfig,
                headers: {
                    ...requestConfig.headers,
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json; charset=utf-8'
                }
            };
        }

        if (requestOptions?.params) {
            requestConfig = {
                ...requestConfig,
                params: requestOptions.params
            };
        }

        return axiosInstanceRef.current.request<T>(requestConfig).pipe(
            map((resp) => resp.data),
            catchError((err: AxiosError) => {
                if (err?.response?.data) {
                    return throwError(err.response.request?.responseText);
                } else if (err?.message) {
                    return throwError(err.message);
                } else {
                    return throwError('Error occurred making call ' + err?.response?.request?.currentRequest?.toString());
                }
            })
        );
    };

    const handleAxiosCall = <T>({
        url,
        method,
        data,
        options: requestOptions
    }: {
        url: string;
        method: Method;
        data?: any;
        options?: IRestRequestOptions;
    }): Observable<T> => {
        let authTokenCall: () => Promise<string>;

        if (getTokenRefresh) {
            authTokenCall = getTokenRefresh;
        } else if (requestOptions?.token) {
            authTokenCall = () => Promise.resolve<string>(requestOptions?.token);
        } else {
            authTokenCall = () => Promise.resolve<string>(null);
        }

        const authObs = from(authTokenCall());

        return authObs.pipe(
            mergeMap((authToken) =>
                buildRequest<T>({
                    url,
                    data,
                    authToken,
                    method,
                    options: requestOptions
                })
            )
        );
    };

    const handleGet = <T>(url: string, requestOptions?: IRestRequestOptions): Observable<T> => {
        return handleAxiosCall<T>({
            url,
            method: 'get',
            options: requestOptions
        });
    };

    const handlePost = <T>(url: string, data?: any, requestOptions?: IRestRequestOptions): Observable<T> => {
        return handleAxiosCall<T>({
            url,
            method: 'post',
            data,
            options: requestOptions
        });
    };

    const handlePut = <T>(url: string, data?: any, requestOptions?: IRestRequestOptions): Observable<T> => {
        return handleAxiosCall<T>({
            url,
            method: 'put',
            data,
            options: requestOptions
        });
    };

    const handlePatch = <T>(url: string, data?: any, requestOptions?: IRestRequestOptions): Observable<T> => {
        return handleAxiosCall<T>({
            url,
            method: 'patch',
            data,
            options: requestOptions
        });
    };

    const handleDelete = <T>(url: string, requestOptions?: IRestRequestOptions): Observable<T> => {
        return handleAxiosCall<T>({
            url,
            method: 'delete',
            options: requestOptions
        });
    };

    return {
        handleGet,
        handlePost,
        handlePut,
        handlePatch,
        handleDelete,
        options
    };
};
